import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../api';
import { useAuth } from '../../contexts/AuthContexte';
import { useNotification } from '../../contexts/NotificationContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { DOFUS_CLASSES, classLabel } from '../../config/dofusClasses';
import PageHeader from '../ui/PageHeader';
import Icon from '../ui/Icon';
import { fmtDate, errorOf, typeLabel, StatusChip, RequestDetails } from './personnageUtils';
import './Personnages.css';

/* ── Déclaration ou modification d'un personnage ── */
const PersonnageModal = ({ serverId, personnage, onClose, onSent }) => {
    const { t } = useLanguage();
    const { success, error: showError } = useNotification();
    const isNew = !personnage;
    const [form, setForm] = useState({
        name: personnage?.name || '',
        classe: personnage?.classe || ''
    });
    const [sending, setSending] = useState(false);
    const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

    const send = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await api.post(`/personnages/${serverId}/requests`, isNew
                ? { type: 'add', ...form }
                : { type: 'edit', personnageId: personnage._id, ...form });
            success(t('personnages.requestSent', 'Demande envoyée : un admin doit la valider'));
            onSent();
        } catch (err) {
            showError(errorOf(err, t('personnages.sendError', 'Envoi de la demande impossible')));
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="c2-modal-backdrop" role="presentation">
            <div className="c2-card c2-modal" role="dialog" aria-modal="true" aria-labelledby="perso-modal-title">
                <div className="c2-modal__head">
                    <h2 id="perso-modal-title">{isNew ? t('personnages.declare', 'Déclarer un personnage') : t('personnages.editTitle', 'Modifier {name}', { name: personnage.name })}</h2>
                    <button type="button" className="c2-icon-btn" onClick={onClose} aria-label={t('common.close', 'Fermer')}>
                        <Icon name="close" size={20} />
                    </button>
                </div>
                <form className="perso-form" onSubmit={send}>
                    <label className="c2-field">
                        <span className="c2-field__label">{t('personnages.name', 'Nom du personnage')}</span>
                        <input className="c2-input" value={form.name} onChange={e => setField('name', e.target.value)} required maxLength={40} autoFocus />
                        <span className="c2-field__hint">{t('personnages.nameHint', 'Exactement comme en jeu : c\'est ce nom que le bot lira sur les screens.')}</span>
                    </label>
                    <label className="c2-field">
                        <span className="c2-field__label">{t('personnages.classe', 'Classe')}</span>
                        <select className="c2-select" value={form.classe} onChange={e => setField('classe', e.target.value)}>
                            <option value="">—</option>
                            {DOFUS_CLASSES.map(c => <option key={c.key} value={c.key}>{t(`classes.${c.key}`, c.label)}</option>)}
                        </select>
                    </label>
                    <div className="c2-modal__actions">
                        <button type="button" className="c2-btn c2-btn--ghost" onClick={onClose} disabled={sending}>{t('common.cancel', 'Annuler')}</button>
                        <button type="submit" className="c2-btn c2-btn--primary" disabled={sending}>
                            <Icon name="check" size={18} />{t('personnages.sendRequest', 'Envoyer la demande')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

/* ── Migration vers un autre serveur ── */
const MigrateModal = ({ serverId, personnage, servers, onClose, onSent }) => {
    const { t } = useLanguage();
    const { success, error: showError } = useNotification();
    const [targetServerId, setTargetServerId] = useState(servers[0]?.id || '');
    const [sending, setSending] = useState(false);

    const send = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await api.post(`/personnages/${serverId}/requests`, { type: 'migrate', personnageId: personnage._id, targetServerId });
            success(t('personnages.migrateSent', 'Demande envoyée : un admin du serveur d\'arrivée doit la valider'));
            onSent();
        } catch (err) {
            showError(errorOf(err, t('personnages.sendError', 'Envoi de la demande impossible')));
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="c2-modal-backdrop" role="presentation">
            <div className="c2-card c2-modal" role="dialog" aria-modal="true" aria-labelledby="perso-migrate-title">
                <div className="c2-modal__head">
                    <h2 id="perso-migrate-title">{t('personnages.migrateTitle', 'Migrer {name}', { name: personnage.name })}</h2>
                    <button type="button" className="c2-icon-btn" onClick={onClose} aria-label={t('common.close', 'Fermer')}>
                        <Icon name="close" size={20} />
                    </button>
                </div>
                {servers.length === 0 ? (
                    <p className="c2-muted">{t('personnages.noOtherServer', 'Tu n\'es membre d\'aucun autre serveur où le bot est présent.')}</p>
                ) : (
                    <form className="perso-form" onSubmit={send}>
                        <p className="c2-muted">{t('personnages.migrateIntro', 'Une fois la demande validée par un admin du serveur d\'arrivée, le personnage est recensé là-bas et n\'est plus lié à ton compte ici.')}</p>
                        <label className="c2-field">
                            <span className="c2-field__label">{t('personnages.targetServer', 'Serveur d\'arrivée')}</span>
                            <select className="c2-select" value={targetServerId} onChange={e => setTargetServerId(e.target.value)}>
                                {servers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        </label>
                        <div className="c2-modal__actions">
                            <button type="button" className="c2-btn c2-btn--ghost" onClick={onClose} disabled={sending}>{t('common.cancel', 'Annuler')}</button>
                            <button type="submit" className="c2-btn c2-btn--primary" disabled={sending || !targetServerId}>
                                <Icon name="swap" size={18} />{t('personnages.sendRequest', 'Envoyer la demande')}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

const PersonnagesMemberPage = () => {
    const { serverId } = useParams();
    const { user } = useAuth();
    const { t } = useLanguage();
    const { success, error: showError } = useNotification();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);     // 'new' ou personnage à modifier
    const [migrating, setMigrating] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        return api.get(`/personnages/${serverId}/mine`)
            .then(res => setData(res.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [serverId]);
    useEffect(() => { load(); }, [load]);

    const otherServers = useMemo(() => {
        const seen = new Set([serverId]);
        return [...(user?.adminGuilds || []), ...(user?.memberGuilds || [])]
            .filter(g => !seen.has(g.id) && seen.add(g.id))
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [user, serverId]);

    const requests = useMemo(() => data?.requests || [], [data]);
    const pendingFor = (personnage) => requests.find(r => r.status === 'pending' && r.personnageId === personnage._id);
    const pendingAdds = requests.filter(r => r.status === 'pending' && r.serverId === serverId && (r.type === 'add' || r.type === 'migrate'));

    const askRemove = async (personnage) => {
        if (!window.confirm(t('personnages.confirmRemove', 'Demander à ne plus être lié à {name} ?', { name: personnage.name }))) return;
        try {
            await api.post(`/personnages/${serverId}/requests`, { type: 'remove', personnageId: personnage._id });
            success(t('personnages.requestSent', 'Demande envoyée : un admin doit la valider'));
            load();
        } catch (err) {
            showError(errorOf(err, t('personnages.sendError', 'Envoi de la demande impossible')));
        }
    };

    const cancelRequest = async (request) => {
        try {
            await api.delete(`/personnages/${serverId}/requests/${request._id}`);
            load();
        } catch (err) {
            showError(errorOf(err, t('personnages.cancelError', 'Annulation impossible')));
        }
    };

    const onSent = () => { setEditing(null); setMigrating(null); load(); };

    return (
        <div className="c2-scope c2-admin-page">
            <PageHeader
                icon="users"
                title={t('personnages.mineTitle', 'Mes personnages')}
                subtitle={t('personnages.mineSubtitle', 'Les personnages que tu joues : le bot s\'en sert pour t\'attribuer les points de tes screens. Chaque changement est validé par un admin.')}
                actions={
                    <button type="button" className="c2-btn c2-btn--primary" onClick={() => setEditing('new')} disabled={!data}>
                        <Icon name="plus" size={18} />{t('personnages.declare', 'Déclarer un personnage')}
                    </button>
                }
            />

            {loading && !data ? (
                <div className="c2-loading"><div className="c2-spinner" /><p>{t('common.loading', 'Chargement...')}</p></div>
            ) : !data ? (
                <p className="c2-empty">{t('personnages.loadError', 'Impossible de charger les personnages.')}</p>
            ) : (
                <>
                    <section className="c2-card c2-section">
                        <div className="c2-section__head">
                            <h2 className="c2-section__title"><Icon name="users" size={20} />{t('personnages.linked', 'Personnages liés à mon compte')}</h2>
                        </div>
                        {data.personnages.length === 0 && pendingAdds.length === 0 ? (
                            <p className="c2-muted">{t('personnages.noneYet', 'Aucun personnage pour l\'instant. Déclare ceux que tu joues sur ce serveur.')}</p>
                        ) : (
                            <ul className="perso-list">
                                {data.personnages.map(p => {
                                    const pending = pendingFor(p);
                                    const info = classLabel(t, p.classe);
                                    return (
                                        <li key={p._id} className="c2-tile perso-item">
                                            <div className="perso-item__main">
                                                <strong>{p.name}</strong>
                                                {info && <span className="c2-muted">{info}</span>}
                                                {p.ownersCount > 1 && <span className="c2-chip" title={t('personnages.sharedHint', 'Un autre joueur a aussi accès à ce personnage')}>{t('personnages.shared', 'Partagé')}</span>}
                                                {pending && <span className="c2-chip c2-chip--purple">{t('personnages.pendingType', '{type} en attente', { type: typeLabel(t, pending.type) })}</span>}
                                            </div>
                                            <div className="perso-item__actions">
                                                <button type="button" className="c2-icon-btn c2-icon-btn--sm" onClick={() => setEditing(p)} disabled={!!pending} title={t('personnages.edit', 'Modifier')} aria-label={t('personnages.edit', 'Modifier')}>
                                                    <Icon name="pencil" size={16} />
                                                </button>
                                                <button type="button" className="c2-icon-btn c2-icon-btn--sm" onClick={() => setMigrating(p)} disabled={!!pending} title={t('personnages.migrate', 'Migrer vers un autre serveur')} aria-label={t('personnages.migrate', 'Migrer vers un autre serveur')}>
                                                    <Icon name="swap" size={16} />
                                                </button>
                                                <button type="button" className="c2-icon-btn c2-icon-btn--sm c2-icon-btn--danger" onClick={() => askRemove(p)} disabled={!!pending} title={t('personnages.remove', 'Ne plus être lié')} aria-label={t('personnages.remove', 'Ne plus être lié')}>
                                                    <Icon name="trash" size={16} />
                                                </button>
                                            </div>
                                        </li>
                                    );
                                })}
                                {pendingAdds.map(r => (
                                    <li key={r._id} className="c2-tile perso-item perso-item--pending">
                                        <div className="perso-item__main">
                                            <RequestDetails t={t} request={r} />
                                            <span className="c2-chip c2-chip--purple">{t('personnages.pendingType', '{type} en attente', { type: typeLabel(t, r.type) })}</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    <section className="c2-card c2-section">
                        <div className="c2-section__head">
                            <h2 className="c2-section__title"><Icon name="history" size={20} />{t('personnages.myRequests', 'Mes demandes')}</h2>
                        </div>
                        {requests.length === 0 ? (
                            <p className="c2-muted">{t('personnages.noRequests', 'Aucune demande.')}</p>
                        ) : (
                            <ul className="perso-requests">
                                {requests.map(r => (
                                    <li key={r._id} className="perso-request">
                                        <span className="c2-chip">{typeLabel(t, r.type)}</span>
                                        <span className="perso-request__details">
                                            <RequestDetails t={t} request={r} />
                                            {r.status === 'rejected' && r.reason && <span className="perso-request__reason">{t('personnages.reasonLabel', 'Motif : {reason}', { reason: r.reason })}</span>}
                                        </span>
                                        <span className="c2-muted perso-request__date">{fmtDate(r.createdAt)}</span>
                                        <StatusChip t={t} status={r.status} />
                                        {r.status === 'pending' ? (
                                            <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm" onClick={() => cancelRequest(r)}>{t('personnages.cancelRequest', 'Annuler')}</button>
                                        ) : <span />}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                </>
            )}

            {editing && (
                <PersonnageModal
                    serverId={serverId}
                    personnage={editing === 'new' ? null : editing}
                    onClose={() => setEditing(null)}
                    onSent={onSent}
                />
            )}
            {migrating && (
                <MigrateModal
                    serverId={serverId}
                    personnage={migrating}
                    servers={otherServers}
                    onClose={() => setMigrating(null)}
                    onSent={onSent}
                />
            )}
        </div>
    );
};

export default PersonnagesMemberPage;
